import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  ChevronDown,
  Factory,
  LayoutGrid,
  Link2,
  Menu,
  Package,
  ShoppingCart,
  Truck,
  X,
} from 'lucide-react';
import {
  publicFooterColumns,
  publicModules,
  whatsappHref,
  hasPublishedDemoVideos,
  formatPlanPrice,
} from '../../data/publicSite';
import logoHorizontalUrl from '../../assets/gastify-logo-horizontal.png';
import isologoStackedUrl from '../../assets/gastify-isologo-stacked.png';
import './publicSite.css';
import { getLaunchSummary, isLaunchOfferActive } from '../../data/billingBrandConfig';

const LAUNCH_OFFER_COPY = isLaunchOfferActive()
  ? `Lanzamiento: ${getLaunchSummary(formatPlanPrice)}.`
  : 'Pagás recién cuando activás tu plan.';

/*
 * Shell del sitio público de Gastify.
 * Sistema visual "gst-*": fondo crema cálido, acento mint (#10B981),
 * tipografía Fraunces para display y Manrope para el resto.
 *
 * Todos los tokens viven scopeados en `.gst-site` (ver publicSite.css):
 * el ERP usa las mismas variables --gst-* en :root con la paleta slate/navy,
 * así que repintarlas globalmente rompería la app.
 */

// ---------- Movimiento ----------

/*
 * Ojo con las dependencias: si el objeto de opciones entra en el array de
 * deps, cada render crea uno nuevo, el efecto se vuelve a correr y el
 * observer se desconecta antes de llegar a disparar (así se quedaban
 * invisibles las secciones de más abajo). Por eso solo dependemos de
 * valores primitivos.
 */
export const useInView = (ref, { threshold = 0.08, rootMargin = '0px' } = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold, rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin]);
  return isVisible;
};

export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'in' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export const CountUp = ({ end, suffix = '', duration = 1400, className = '' }) => {
  const ref = useRef(null);
  const visible = useInView(ref);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!visible) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, end, duration]);

  return <span ref={ref} className={className}>{value}{suffix}</span>;
};

export { whatsappHref, hasPublishedDemoVideos };

// ---------- Nav ----------

const MODULE_ICONS = {
  'shopping-cart': ShoppingCart,
  truck: Truck,
  package: Package,
  factory: Factory,
  'bar-chart': BarChart3,
  link: Link2,
};

export const registroHref = '/registro-onboarding';

const defaultFooterCta = {
  title: 'Probá Gastify gratis, sin tarjeta.',
  description: `Creás tu cuenta en minutos. ${LAUNCH_OFFER_COPY}`,
  primaryLabel: 'Empezar prueba',
  primaryHref: registroHref,
  secondaryLabel: 'Ver precios',
  secondaryHref: '/precios',
  secondaryExternal: false,
};

const isModulePath = (path) => path.startsWith('/funcionalidades');

const FooterLink = ({ href, external, label }) =>
  external ? (
    <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
  ) : (
    <Link to={href}>{label}</Link>
  );

const PublicNav = () => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);

  return (
    <nav className="gst-nav">
      <div className="gst-nav-inner">
        <Link className="gst-logo" to="/" onClick={close}>
          <img src={logoHorizontalUrl} alt="Gastify" />
        </Link>

        <div className="gst-nav-links">
          <Link to="/" className={pathname === '/' ? 'active' : ''}>Inicio</Link>
          <div className={`gst-nav-dd${isModulePath(pathname) ? ' active' : ''}`}>
            <button type="button" className="gst-nav-dd-btn">
              Funcionalidades <ChevronDown size={14} />
            </button>
            <div className="gst-nav-dd-menu">
              {publicModules.map((mod) => {
                const Icon = MODULE_ICONS[mod.icon] || LayoutGrid;
                return (
                  <Link key={mod.key} to={mod.href} className={pathname === mod.href ? 'current' : ''}>
                    <Icon size={16} />{mod.label}
                  </Link>
                );
              })}
              <Link to="/funcionalidades" className="dd-all">
                <LayoutGrid size={16} />Ver todas las funcionalidades
              </Link>
            </div>
          </div>
          <Link to="/precios" className={pathname === '/precios' ? 'active' : ''}>Precios</Link>
        </div>

        <div className="gst-nav-actions">
          <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/login">Ingresar</Link>
          <Link className="gst-btn gst-btn-primary gst-btn-sm" to={registroHref}>
            Empezar prueba <ArrowRight size={14} />
          </Link>
        </div>

        <button
          type="button"
          className="gst-nav-burger"
          aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <div className={`gst-nav-mobile${mobileOpen ? ' open' : ''}`}>
        <Link to="/" onClick={close}>Inicio</Link>
        <Link to="/funcionalidades" onClick={close}>Funcionalidades</Link>
        {publicModules.map((mod) => (
          <Link key={mod.key} to={mod.href} className="sub" onClick={close}>{mod.label}</Link>
        ))}
        <Link to="/precios" onClick={close}>Precios</Link>
        <div className="actions">
          <Link className="gst-btn gst-btn-ghost gst-btn-block" to="/login" onClick={close}>Ingresar</Link>
          <Link className="gst-btn gst-btn-primary gst-btn-block" to={registroHref} onClick={close}>
            Empezar prueba <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </nav>
  );
};

export const FooterCta = ({ cta = defaultFooterCta }) => {
  const resolved = { ...defaultFooterCta, ...(cta || {}) };
  return (
    <section className="gst-container" style={{ padding: '48px 0 56px' }}>
      <Reveal className="gst-footer-cta">
        <div className="gst-footer-cta-grid">
          <div>
            {resolved.eyebrow && <div className="gst-eyebrow gst-eyebrow-mint">{resolved.eyebrow}</div>}
            <h2>{resolved.title}</h2>
            <p>{resolved.description}</p>
          </div>
          <div className="gst-footer-cta-actions">
            <Link className="gst-btn gst-btn-dark" to={resolved.primaryHref}>
              {resolved.primaryLabel} <ArrowRight size={16} />
            </Link>
            {resolved.secondaryLabel && (
              resolved.secondaryExternal ? (
                <a className="gst-btn gst-btn-ghost" href={resolved.secondaryHref} target="_blank" rel="noopener noreferrer">
                  {resolved.secondaryLabel}
                </a>
              ) : (
                <Link className="gst-btn gst-btn-ghost" to={resolved.secondaryHref}>{resolved.secondaryLabel}</Link>
              )
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const PublicFooter = () => (
  <footer className="gst-footer">
    <div className="gst-footer-cols">
      <div style={{ maxWidth: '24rem' }}>
        <img src={isologoStackedUrl} alt="Gastify" style={{ height: 80, width: 'auto', display: 'block' }} />
        <p style={{ marginTop: 16, fontSize: 14, lineHeight: 1.6, color: 'var(--gst-graphite-700)' }}>
          Gastify reúne operación, caja, stock y decisiones de negocio en una experiencia pensada
          para equipos que necesitan velocidad sin perder control.
        </p>
      </div>
      {publicFooterColumns.map((col) => (
        <div key={col.title}>
          <h3>{col.title}</h3>
          {col.links.map((link) => (
            <FooterLink key={link.label} {...link} />
          ))}
        </div>
      ))}
    </div>
    <div className="gst-footer-bottom">
      <p>© {new Date().getFullYear()} Gastify. Todos los derechos reservados.</p>
      <p>Hecho para negocios que necesitan vender más ordenados y decidir con mayor claridad.</p>
    </div>
  </footer>
);

export default function PublicSiteShell({ children, footerCta = defaultFooterCta, hideFooterCta = false }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="gst-site">
      <div className="gst-halo" />
      <PublicNav />
      <main>
        {children}
        {!hideFooterCta && <FooterCta cta={footerCta} />}
      </main>
      <PublicFooter />
    </div>
  );
}
